/**
 * Public Status Page - Öffentliche Statusseite
 */
import React, { useState, useEffect } from 'react';
import { Card, Badge, Spinner, Alert, Row, Col, Button } from 'react-bootstrap';
import { getPublicOverview, getStatusSummary } from '../../services/api';
import UptimeBar from '../common/UptimeBar';
import EndpointDetailsModal from '../common/EndpointDetailsModal';

function PublicStatus() {
  const [endpoints, setEndpoints] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState(null);

  useEffect(() => {
    loadData();
    // Auto-refresh alle 30 Sekunden
    const interval = setInterval(loadData, 30000);
    return () => clearInterval(interval);
  }, []);

  const loadData = async () => {
    try {
      setError(null);
      const [overviewRes, summaryRes] = await Promise.all([
        getPublicOverview(),
        getStatusSummary(),
      ]);
      setEndpoints(overviewRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error('Fehler beim Laden:', err);
      setError('Fehler beim Laden der Statusdaten. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      up: 'success',
      down: 'danger',
      unknown: 'secondary',
    };
    const labels = {
      up: 'Operational',
      down: 'Down',
      unknown: 'Unknown',
    };
    return (
      <Badge bg={variants[status] || 'secondary'} className="px-3 py-2">
        {labels[status] || status}
      </Badge>
    );
  };

  const getUptimeColor = (percentage) => {
    if (percentage === null || percentage === undefined) return '#6c757d';
    if (percentage === 100) return '#28a745';
    if (percentage >= 99) return '#9cbf2b';
    if (percentage >= 95) return '#ffc107';
    if (percentage >= 85) return '#fd7e14';
    return '#dc3545';
  };

  const handleShowDetails = (endpoint) => {
    setSelectedEndpoint(endpoint);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedEndpoint(null);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="success" className="spinner-pcs" />
        <p className="mt-3">Lade Statusdaten...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="status-header">
        <div className="status-header-top">
          <div>
            <h1>
              <span className={`status-dot${summary?.degraded > 0 ? ' down' : ''}`} />
              System Status
            </h1>
            <p>
              {summary?.degraded > 0
                ? 'Es liegt eine Störung vor'
                : 'Alle Systeme operativ'}
            </p>
          </div>
          <a
            href={`${process.env.REACT_APP_API_URL || '/api/v1'}/public/report/pdf`}
            className="btn-report"
            title="Verfügbarkeitsbericht als PDF herunterladen (90 Tage)"
            download
          >
            <svg width="15" height="15" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
              <path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/>
              <path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/>
            </svg>
            PDF-Bericht
          </a>
        </div>
        {summary && (
          <div className="status-summary">
            <span><strong>{summary.operational}</strong> Operational</span>
            <span><strong>{summary.degraded}</strong> Degraded</span>
            <span><strong>{summary.global_uptime_7d}%</strong> Uptime (7d)</span>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Endpoints */}
      {endpoints.length === 0 ? (
        <Alert variant="info">
          Noch keine Endpoints konfiguriert.
        </Alert>
      ) : (
        endpoints.map((endpoint) => (
          <Card key={endpoint.id} className="endpoint-card">
            {/* Header */}
            <div className="endpoint-header">
              <div style={{ flex: 1 }}>
                <div className="endpoint-name">
                  {endpoint.name}
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="ms-2"
                    onClick={() => handleShowDetails(endpoint)}
                    title="Detaillierte Uptime-Informationen anzeigen"
                  >
                    <i className="bi bi-info-circle"></i> Details
                  </Button>
                </div>
                <div className="endpoint-url">{endpoint.url}</div>
                {endpoint.sensor_type && endpoint.sensor_type !== 'http' && (
                  <Badge bg="info" className="mt-1 me-1">{endpoint.sensor_type.toUpperCase()}</Badge>
                )}
                {endpoint.group && (
                  <Badge bg="secondary" className="mt-1">{endpoint.group}</Badge>
                )}
              </div>
              <div style={{ textAlign: 'right' }}>
                {getStatusBadge(endpoint.current_status)}
                {/* Prominente Uptime-Anzeige wie bei Claude */}
                <div style={{ marginTop: '8px' }}>
                  <span
                    style={{
                      fontSize: '1.5rem',
                      fontWeight: '700',
                      color: getUptimeColor(endpoint.uptime_percentage_90d),
                    }}
                  >
                    {endpoint.uptime_percentage_90d?.toFixed(2) || '0.00'}%
                  </span>
                  <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: '2px' }}>
                    90 Tage Uptime
                  </div>
                </div>
              </div>
            </div>

            {/* Uptime Bar */}
            <Card.Body>
              <div className="mb-4">
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '12px'
                }}>
                  <span style={{ fontSize: '0.9rem', fontWeight: '600', color: '#495057' }}>
                    Verfügbarkeit der letzten 90 Tage
                  </span>
                  {endpoint.last_check && (
                    <small className="text-muted">
                      Letzter Check: {new Date(endpoint.last_check).toLocaleString('de-DE')}
                    </small>
                  )}
                </div>
                <UptimeBar endpointId={endpoint.id} days={90} />
              </div>

              {/* Additional Stats */}
              <Row className="text-center mt-4">
                <Col md={3} sm={6} className="mb-3 mb-md-0">
                  <div className="stat-item">
                    <div
                      className="stat-value"
                      style={{ color: getUptimeColor(endpoint.uptime_percentage_7d) }}
                    >
                      {endpoint.uptime_percentage_7d?.toFixed(2) || '0.00'}%
                    </div>
                    <div className="stat-label">7 Tage</div>
                  </div>
                </Col>
                <Col md={3} sm={6} className="mb-3 mb-md-0">
                  <div className="stat-item">
                    <div
                      className="stat-value"
                      style={{ color: getUptimeColor(endpoint.uptime_percentage_30d) }}
                    >
                      {endpoint.uptime_percentage_30d?.toFixed(2) || '0.00'}%
                    </div>
                    <div className="stat-label">30 Tage</div>
                  </div>
                </Col>
                <Col md={3} sm={6} className="mb-3 mb-md-0">
                  <div className="stat-item">
                    <div
                      className="stat-value"
                      style={{ color: getUptimeColor(endpoint.uptime_percentage_90d) }}
                    >
                      {endpoint.uptime_percentage_90d?.toFixed(2) || '0.00'}%
                    </div>
                    <div className="stat-label">90 Tage</div>
                  </div>
                </Col>
                <Col md={3} sm={6}>
                  <div className="stat-item">
                    <div className="stat-value" style={{ color: '#9cbf2b' }}>
                      {endpoint.avg_response_time ? `${Math.round(endpoint.avg_response_time)}ms` : 'N/A'}
                    </div>
                    <div className="stat-label">Ø Response</div>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))
      )}

      {/* Last Updated */}
      <div className="text-center text-muted mt-4 mb-4">
        <small>
          Aktualisiert: {new Date().toLocaleString('de-DE')} ·
          Auto-Refresh alle 30 Sekunden
        </small>
      </div>

      {/* Endpoint Details Modal */}
      {selectedEndpoint && (
        <EndpointDetailsModal
          show={showDetailsModal}
          onHide={handleCloseDetails}
          endpointId={selectedEndpoint.id}
          endpointName={selectedEndpoint.name}
        />
      )}
    </div>
  );
}

export default PublicStatus;
